import { useCallback, useRef, useState } from 'react';

import { useDidUpdate } from '@toolbox/hooks';

import type { AnyAction, BaseAction } from '../reducer/types';

export interface Dispatch<A extends BaseAction = AnyAction> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  <T extends A>(action: T, ...extraArgs: any[]): T;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface MiddlewareAPI<S, D extends Dispatch<any>> {
  getState: () => S;
  dispatch: D;
}

export type Middleware<
  S,
  A extends BaseAction = BaseAction,
  D extends Dispatch<A> = Dispatch<A>,
> = (store: MiddlewareAPI<S, D>) => (next: Dispatch<A>) => D;

export const useEnhancedReducer = <A extends BaseAction, S, D extends Dispatch<A> = Dispatch<A>>(
  reducer: (state: S, action: A) => S,
  initialState: S | (() => S),
  middlewares: Middleware<S, A, D>[],
): [S, D] => {
  const [state, setState] = useState(initialState);
  const stateRef = useRef(state);

  const dispatch: Dispatch<A> = useCallback(
    (action) => {
      stateRef.current = reducer(stateRef.current, action);
      setState(stateRef.current);
      return action;
    },
    [reducer],
  );

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
  const dispatchRef = useRef<D>();

  const createDispatch = (): D => {
    return composeMiddleware<S, A, D>(middlewares)(
      {
        getState: () => stateRef.current,
        dispatch: ((...args: [A]) => dispatchRef.current?.(...args)) as D,
      },
      dispatch as D,
    );
  };

  useDidUpdate(() => {
    dispatchRef.current = createDispatch();
  }, [dispatch, ...middlewares]);

  const getDispatch = (): D => {
    if (!dispatchRef.current) {
      dispatchRef.current = createDispatch();
    }

    return dispatchRef.current;
  };

  return [stateRef.current, getDispatch()];
};

function composeMiddleware<S, A extends BaseAction, D extends Dispatch<A>>(
  chain: Middleware<S, A, D>[],
) {
  return (context: MiddlewareAPI<S, D>, dispatch: D): D => {
    return chain.reduceRight((res, middleware) => {
      return middleware(context)(res);
    }, dispatch);
  };
}
